import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { FileText, Plus, X } from "lucide-react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { PeopleSelector } from "@/shared/components/PeopleSelector";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { findContactIdByName } from "@/shared/utils/confirmedEstateRecords";
import { contactFullName, formatContactName } from "@/shared/utils/contactUtils";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type {
  FamilyTriagePersonRow,
  HasChildrenAnswer,
  PersonFlowStatus,
} from "@/shared/types/probateTypes";

import { createEmptyRow } from "./IssueRow";
import {
  getFirstName,
  getLastName,
  setFirstName,
  setLastName,
  NOT_SURE_CAPTION,
} from "./nameUtils";
import { useJsonListAnswer } from "./useJsonListAnswer";

const CHILDREN_KEY = "family_triage_has_children";
const CHILDREN_LIST_KEY = "family_triage_children_list";

function createEmptyChild(): FamilyTriagePersonRow {
  return { ...createEmptyRow(), isMinor: "" };
}

export const ChildrenScreen: FC<FlowCustomComponentProps> = ({ answers, setAnswer, errors }) => {
  const { person } = useAppContext();
  const { contacts, updateContact } = useUserContacts();
  const name = person?.first_name ?? "the decedent";
  const nameRef = useRef(name);
  nameRef.current = name;
  const seededRef = useRef(false);

  const hasErrors = !!errors && Object.keys(errors).length > 0;
  const [seededFromWill, setSeededFromWill] = useState(false);
  const rawHasChildren = (answers[CHILDREN_KEY] as HasChildrenAnswer) ?? "";
  const hasChildren = (rawHasChildren || (seededFromWill ? "yes" : "")) as HasChildrenAnswer;

  const [children, syncChildren] = useJsonListAnswer<FamilyTriagePersonRow>(
    answers,
    CHILDREN_LIST_KEY,
    setAnswer,
  );

  useEffect(() => {
    if (seededRef.current || hasChildren || children.length > 0) return;
    const willChildren = contacts.filter(
      (c) => c.extracted_from_document && c.relationship?.toLowerCase() === "child",
    );
    if (willChildren.length === 0) return;
    seededRef.current = true;

    const seeded: FamilyTriagePersonRow[] = willChildren.map((c) => ({
      ...createEmptyRow(),
      name: formatContactName(c.first_name, c.last_name),
      isMinor: "",
      sourceTag: `From ${nameRef.current}'s will`,
      contactId: c.id,
    }));

    setAnswer(CHILDREN_KEY, "yes");
    syncChildren(seeded);
    setSeededFromWill(true);
  }, [contacts, hasChildren, setAnswer, syncChildren]);

  useEffect(() => {
    if (!rawHasChildren && children.length > 0) {
      setAnswer(CHILDREN_KEY, "yes");
    }
  }, [rawHasChildren, children.length, setAnswer]);

  const handleChildrenSelect = (value: string) => {
    const val = value as HasChildrenAnswer;
    setAnswer(CHILDREN_KEY, val);
    if (val === "yes" && children.length === 0) {
      syncChildren([createEmptyChild()]);
    } else if (val !== "yes") {
      syncChildren([]);
      setSeededFromWill(false);
      seededRef.current = false;
    }
  };

  const handleChildChange = (index: number, updated: FamilyTriagePersonRow) => {
    const next = [...children];
    next[index] = updated;
    syncChildren(next);
  };

  // Rows saved before the selector only have a typed name, so match them to a contact.
  const childIds = useMemo(
    () => children.map((child) => child.contactId ?? findContactIdByName(contacts, child.name)),
    [children, contacts],
  );

  useEffect(() => {
    if (children.some((child, i) => !child.contactId && childIds[i])) {
      syncChildren(children.map((child, i) => ({ ...child, contactId: childIds[i] ?? undefined })));
    }
  }, [children, childIds, syncChildren]);

  const withContact = (
    child: FamilyTriagePersonRow,
    contactId: number | null,
  ): FamilyTriagePersonRow => {
    const contact = contacts.find((c) => c.id === contactId);
    return {
      ...child,
      contactId: contact?.id,
      name: contactFullName(contact),
    };
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Will-seeded banner */}
      {seededFromWill && (
        <div className="flex items-start gap-3 rounded-lg border border-brand bg-brand-subtle p-4">
          <FileText className="mt-0.5 size-5 shrink-0 text-brand" />
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">We filled this in from {name}'s will</p>
            <p className="text-sm text-secondary">
              Review each answer and confirm it's right, or make a change.
            </p>
          </div>
        </div>
      )}

      {/* Children section */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="font-sans text-xl font-semibold leading-[30px]">
            Did {name} have any children?
          </p>
          <p className="text-sm text-secondary tracking-[-0.28px]">
            Include children from any relationship (biological and adopted). The court asks for each
            of them.
          </p>
        </div>

        <RadioGroup
          name="has_children"
          value={hasChildren}
          onValueChange={handleChildrenSelect}
          className="flex flex-col gap-3"
        >
          <OptionCard
            label="Yes"
            selected={hasChildren === "yes"}
            onClick={() => handleChildrenSelect("yes")}
            variant="radio"
            value="yes"
          />
          <OptionCard
            label="No children"
            selected={hasChildren === "no_children"}
            onClick={() => handleChildrenSelect("no_children")}
            variant="radio"
            value="no_children"
          />
        </RadioGroup>
        <FieldError message={errors?.has_children} />

        {hasChildren === "yes" && (
          <div className="flex flex-col gap-4">
            <div className="border-t border-primary" />

            <p className="font-sans text-xl font-semibold leading-[30px]">
              Tell us about {name}'s children
            </p>

            {children.map((child, i) => {
              const childFirstName = getFirstName(child.name) || "this person";
              const childContact = contacts.find((c) => c.id === childIds[i]);
              return (
                <div
                  key={child.id}
                  className="flex flex-col gap-3 rounded-lg border border-primary p-4"
                >
                  <PeopleSelector
                    mode="single"
                    label="Name"
                    placeholder={child.name || undefined}
                    newPersonName={child.name}
                    value={childIds[i]}
                    onChange={(contactId) => handleChildChange(i, withContact(child, contactId))}
                    filter={(contact) =>
                      contact.id === childIds[i] || !childIds.includes(contact.id)
                    }
                    error={errors?.[`child_${i}_first_name`] ?? errors?.[`child_${i}_last_name`]}
                  />

                  {childContact && !childContact.last_name?.trim() && (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={`child-${child.id}-last-name`}>
                        Last name for {childFirstName}
                      </Label>
                      <Input
                        id={`child-${child.id}-last-name`}
                        placeholder="Last name"
                        onBlur={(e) => {
                          const lastName = e.target.value.trim();
                          if (lastName) {
                            updateContact({ id: childContact.id, last_name: lastName });
                            handleChildChange(i, {
                              ...child,
                              name: contactFullName({ ...childContact, last_name: lastName }),
                            });
                          }
                        }}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <Label>Are they under 18 years old?</Label>
                      <Select
                        value={child.isMinor}
                        onValueChange={(v) =>
                          handleChildChange(i, { ...child, isMinor: v as "yes" | "no" })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label>Status</Label>
                      <Select
                        value={child.status}
                        onValueChange={(v) => {
                          const status = v as PersonFlowStatus;
                          if (status !== "passed_away") {
                            handleChildChange(i, {
                              ...child,
                              status,
                              deathYear: "",
                              hasChildren: "",
                              children: [],
                            });
                          } else {
                            handleChildChange(i, { ...child, status });
                          }
                        }}
                      >
                        <SelectTrigger
                          className={errors?.[`child_${i}_status`] ? "border-destructive" : ""}
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="living">Living</SelectItem>
                          <SelectItem value="passed_away">Passed away</SelectItem>
                          <SelectItem value="not_sure">Not sure</SelectItem>
                        </SelectContent>
                      </Select>
                      <FieldError message={errors?.[`child_${i}_status`]} />
                    </div>
                  </div>

                  {child.status === "passed_away" && (
                    <>
                      <div className="flex flex-col gap-1.5">
                        <Label>Did {childFirstName} have children?</Label>
                        <Select
                          value={child.hasChildren}
                          onValueChange={(v) => {
                            const val = v as "yes" | "no" | "not_sure";
                            if (val === "yes" && child.children.length === 0) {
                              handleChildChange(i, {
                                ...child,
                                hasChildren: val,
                                children: [createEmptyRow()],
                              });
                            } else if (val !== "yes") {
                              handleChildChange(i, { ...child, hasChildren: val, children: [] });
                            } else {
                              handleChildChange(i, { ...child, hasChildren: val });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yes">Yes</SelectItem>
                            <SelectItem value="no">No</SelectItem>
                            <SelectItem value="not_sure">Not sure</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-secondary">
                          If {childFirstName} did, their children take that place on the court's
                          list.
                        </p>
                      </div>

                      {child.hasChildren === "yes" && (
                        <div className="flex flex-col gap-4">
                          <p className="font-sans text-lg font-semibold">
                            Add {childFirstName}'s children
                          </p>

                          {child.children.map((grandchild, gi) => {
                            const gcFirstErr =
                              hasErrors && !getFirstName(grandchild.name).trim()
                                ? "First name is required"
                                : undefined;
                            const gcLastErr =
                              hasErrors && !getLastName(grandchild.name).trim()
                                ? "Last name is required"
                                : undefined;
                            const gcStatusErr =
                              hasErrors && !grandchild.status ? "Status is required" : undefined;

                            const canRemoveGrandchild = child.children.length > 1;

                            return (
                              <div
                                key={grandchild.id}
                                className="relative rounded-lg border border-primary bg-subtle p-4"
                              >
                                {canRemoveGrandchild && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleChildChange(i, {
                                        ...child,
                                        children: child.children.filter((_, j) => j !== gi),
                                      });
                                    }}
                                    className="absolute right-3 top-3 rounded-md p-1 text-tertiary hover:bg-subtle hover:text-primary"
                                    aria-label="Remove"
                                  >
                                    <X className="size-4" />
                                  </button>
                                )}
                                <div
                                  className={`flex flex-col gap-3 ${canRemoveGrandchild ? "pr-6" : ""}`}
                                >
                                  <div className="grid grid-cols-2 gap-3">
                                    <div className="flex flex-col gap-1.5">
                                      <Label>First name</Label>
                                      <Input
                                        placeholder="First name"
                                        value={getFirstName(grandchild.name)}
                                        onChange={(e) => {
                                          const nextChildren = [...child.children];
                                          nextChildren[gi] = {
                                            ...grandchild,
                                            name: setFirstName(grandchild.name, e.target.value),
                                          };
                                          handleChildChange(i, {
                                            ...child,
                                            children: nextChildren,
                                          });
                                        }}
                                        className={gcFirstErr ? "border-destructive" : ""}
                                      />
                                      <FieldError message={gcFirstErr} />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                      <Label>Last name</Label>
                                      <Input
                                        placeholder="Last name"
                                        value={getLastName(grandchild.name)}
                                        onChange={(e) => {
                                          const nextChildren = [...child.children];
                                          nextChildren[gi] = {
                                            ...grandchild,
                                            name: setLastName(grandchild.name, e.target.value),
                                          };
                                          handleChildChange(i, {
                                            ...child,
                                            children: nextChildren,
                                          });
                                        }}
                                        className={gcLastErr ? "border-destructive" : ""}
                                      />
                                      <FieldError message={gcLastErr} />
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-2 gap-3">
                                    <div className="flex flex-col gap-1.5">
                                      <Label>Are they under 18 years old?</Label>
                                      <Select
                                        value={grandchild.isMinor}
                                        onValueChange={(v) => {
                                          const nextChildren = [...child.children];
                                          nextChildren[gi] = {
                                            ...grandchild,
                                            isMinor: v as "yes" | "no",
                                          };
                                          handleChildChange(i, {
                                            ...child,
                                            children: nextChildren,
                                          });
                                        }}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="yes">Yes</SelectItem>
                                          <SelectItem value="no">No</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                      <Label>Status</Label>
                                      <Select
                                        value={grandchild.status}
                                        onValueChange={(v) => {
                                          const nextChildren = [...child.children];
                                          const status = v as PersonFlowStatus;
                                          nextChildren[gi] =
                                            status !== "passed_away"
                                              ? {
                                                  ...grandchild,
                                                  status,
                                                  deathYear: "",
                                                  hasChildren: "",
                                                  children: [],
                                                }
                                              : { ...grandchild, status };
                                          handleChildChange(i, {
                                            ...child,
                                            children: nextChildren,
                                          });
                                        }}
                                      >
                                        <SelectTrigger
                                          className={gcStatusErr ? "border-destructive" : ""}
                                        >
                                          <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="living">Living</SelectItem>
                                          <SelectItem value="passed_away">Passed away</SelectItem>
                                          <SelectItem value="not_sure">Not sure</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <FieldError message={gcStatusErr} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          <button
                            type="button"
                            onClick={() =>
                              handleChildChange(i, {
                                ...child,
                                children: [...child.children, createEmptyRow()],
                              })
                            }
                            className="flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
                          >
                            <Plus className="size-3.5" />
                            Add another child
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {child.status === "not_sure" && (
                    <p className="text-xs text-secondary">{NOT_SURE_CAPTION}</p>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              onClick={() => syncChildren([...children, createEmptyChild()])}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary py-3 text-sm font-medium text-primary hover:bg-subtle"
            >
              <Plus className="size-4" />
              Add another
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
